/*
 * SecurePdfView — renders a "Secure PDF" book: per-page PNG images + a rebuilt
 * selectable text layer (from index.json). The source PDF never reaches the browser.
 * Routed in by BookView when {EBookPath}{ISBN}/index.json exists in blob.
 *
 * Chrome is built to match the HTML eBook viewer as closely as possible: grey stage,
 * drop-shadowed two-page spread, tall white side-arrows, round close button, and the
 * same bottom .toolbar (markup tools left, rc-slider + page bubble center, one/two-page
 * toggle right) reusing the app's global toolbar classes, icons and cursors.
 *
 * Markups (highlight 4 colors / underline / strikethrough / eraser) are anchored to
 * the text overlay and persisted DEVICE-LOCAL (localStorage by ISBN) — correct for the
 * shared login, where DB-stored markups would leak between people.
 */
import React, { Component } from "react";
import Slider from "rc-slider";
import config from "../../api/config";

// reuse the exact HTML-viewer icons so the toolbar is visually identical
import icon_pointer from "../../images/arrow-big-top.svg";
import icon_highlight from "../../images/highlight.svg";
import icon_strikethrough from "../../images/strikethrough.svg";
import icon_underline from "../../images/underline.svg";
import icon_eraser from "../../images/eraser.svg";
import icon_file from "../../images/file.svg";
import icon_pages from "../../images/pages.svg";
import icon_cross from "../../images/cross.svg";
import icon_navarrow from "../../images/icon_arrow.svg";
import icon_zoom_in from "../../images/zoom_in.svg";
import icon_zoom_out from "../../images/zoom_out.svg";
// custom highlight/eraser cursors (same as .pages hl-mode in Pages.scss)
import cur_yellow from "../../images/icon_hl_yellow_cursor.png";
import cur_orange from "../../images/icon_hl_orange_cursor.png";
import cur_green from "../../images/icon_hl_green_cursor.png";
import cur_teal from "../../images/icon_hl_teal_cursor.png";
import cur_eraser from "../../images/icon_eraser_cursor.png";

const HL_COLORS = [
  { name: "yellow", hex: "#ffea01", cursor: cur_yellow },
  { name: "orange", hex: "#ff8f02", cursor: cur_orange },
  { name: "green", hex: "#63e60d", cursor: cur_green },
  { name: "teal", hex: "#09d8f8", cursor: cur_teal },
];

export default class SecurePdfView extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loaded: false,
      error: "",
      numPages: 0,
      spreadIdx: 0,
      twoPage: true,
      zoom: 1,
      tool: "pointer", // pointer | highlight | underline | strike | eraser
      hlColor: "#ffea01",
      hlMenuOpen: false,
    };
    this.index = null; // index.json { numPages, pages:[{w,h,png,text:[{s,l,t,h,w}]}] }
    this.spreads = [];
    this.markups = {}; // pageNum -> [{type,color,rects:[{x,y,w,h}]}] (normalized)
    this.dpr = Math.max(1, window.devicePixelRatio || 1);
    this.renderToken = 0;
    this.stageRef = React.createRef();
    this.pageViewRef = React.createRef();
    this.prevNavRef = React.createRef();
    this.nextNavRef = React.createRef();
    this.isbn = props.book.ISBN;
    this.base = `${config.API.Storage}${props.book.EBookPath}${props.book.ISBN}/`;
    this.token = props.token || "";
  }

  componentDidMount() {
    this.load();
    window.addEventListener("resize", this.onResize);
    document.addEventListener("keydown", this.onKey);
  }
  componentWillUnmount() {
    window.removeEventListener("resize", this.onResize);
    document.removeEventListener("keydown", this.onKey);
  }

  // ---- persistence: device-local only (shared login => never the DB) ----
  mkKey = () => `dibs:securepdf:markups:${this.isbn}`;
  loadMarkups = () => {
    try {
      const r = localStorage.getItem(this.mkKey());
      this.markups = r ? JSON.parse(r) : {};
    } catch (e) {
      this.markups = {};
    }
  };
  saveMarkups = () => {
    try {
      localStorage.setItem(this.mkKey(), JSON.stringify(this.markups));
    } catch (e) {}
  };

  load = async () => {
    try {
      const res = await fetch(this.base + "index.json" + this.token);
      if (!res.ok) throw new Error("index.json HTTP " + res.status);
      this.index = await res.json();
      this.loadMarkups();
      this.spreads = this.buildSpreads(this.index.numPages);
      this.setState({ loaded: true, numPages: this.index.numPages }, () =>
        this.renderSpread(),
      );
    } catch (e) {
      console.error("SecurePdfView load failed", e);
      this.setState({ error: e.message || "Failed to load book" });
    }
  };

  // book-style spread map: cover alone, then pairs
  buildSpreads = (n) => {
    const s = [];
    if (!this.state.twoPage) {
      for (let p = 1; p <= n; p++) s.push([p]);
      return s;
    }
    s.push([1]);
    for (let p = 2; p <= n; p += 2) s.push(p + 1 <= n ? [p, p + 1] : [p]);
    return s;
  };

  // transparent, selectable text layer from stored items (mirrors pdf.js TextLayer scaleX math)
  buildTextLayer = (tlDiv, items, scale) => {
    for (const it of items) {
      if (!it.s) continue;
      const span = document.createElement("span");
      span.textContent = it.s;
      span.style.left = it.l * scale + "px";
      span.style.top = it.t * scale + "px";
      span.style.fontSize = it.h * scale + "px";
      span.style.fontFamily = "sans-serif";
      tlDiv.append(span);
      const target = it.w * scale,
        measured = span.getBoundingClientRect().width;
      if (measured > 0 && target > 0)
        span.style.transform = `scaleX(${target / measured})`;
    }
  };

  drawMarkups = (pageNum, wrap) => {
    const layer = wrap.querySelector(".spdf-markup");
    layer.innerHTML = "";
    const W = wrap.clientWidth,
      H = wrap.clientHeight;
    const erasing = this.state.tool === "eraser";
    for (const m of this.markups[pageNum] || []) {
      for (const r of m.rects) {
        const d = document.createElement("div");
        d.className = "spdf-mk " + (m.type === "highlight" ? "hl" : m.type);
        const x = r.x * W,
          y = r.y * H,
          w = r.w * W,
          h = r.h * H;
        d.style.left = x + "px";
        d.style.top = y + "px";
        d.style.width = w + "px";
        d.style.height = h + "px";
        if (m.type === "highlight") d.style.background = m.color;
        else if (m.type === "underline")
          d.style.borderBottom = `2px solid ${m.color}`;
        else if (m.type === "strike") {
          d.style.borderTop = `2px solid ${m.color}`;
          d.style.top = y + h / 2 - 1 + "px";
          d.style.height = "0";
        }
        d.onclick = () => {
          if (this.state.tool === "eraser") {
            m.rects = [];
            this.markups[pageNum] = (this.markups[pageNum] || []).filter(
              (x) => x.rects.length,
            );
            this.drawMarkups(pageNum, wrap);
            this.saveMarkups();
          }
        };
        layer.append(d);
      }
    }
    layer.classList.toggle("erasing", erasing);
  };

  captureSelection = (pageNum, wrap, ev) => {
    const tool = this.state.tool;
    if (tool !== "highlight" && tool !== "underline" && tool !== "strike")
      return;
    const sel = window.getSelection();
    // single click (collapsed): expand to the word under the pointer
    if ((!sel || sel.isCollapsed) && ev) {
      let node = null,
        off = 0;
      if (document.caretPositionFromPoint) {
        const p = document.caretPositionFromPoint(ev.clientX, ev.clientY);
        if (p) {
          node = p.offsetNode;
          off = p.offset;
        }
      } else if (document.caretRangeFromPoint) {
        const rr = document.caretRangeFromPoint(ev.clientX, ev.clientY);
        if (rr) {
          node = rr.startContainer;
          off = rr.startOffset;
        }
      }
      if (node && node.nodeType === 3) {
        const txt = node.textContent;
        let a = off,
          b = off;
        while (a > 0 && /\S/.test(txt[a - 1])) a--;
        while (b < txt.length && /\S/.test(txt[b])) b++;
        if (b > a) {
          const range = document.createRange();
          range.setStart(node, a);
          range.setEnd(node, b);
          sel.removeAllRanges();
          sel.addRange(range);
        }
      }
    }
    if (!sel || sel.isCollapsed) return;
    const wrapRect = wrap.getBoundingClientRect();
    const rects = [];
    for (const r of sel.getRangeAt(0).getClientRects()) {
      const cx = r.left + r.width / 2,
        cy = r.top + r.height / 2;
      if (
        cx < wrapRect.left ||
        cx > wrapRect.right ||
        cy < wrapRect.top ||
        cy > wrapRect.bottom
      )
        continue;
      if (r.width < 1 || r.height < 1) continue;
      rects.push({
        x: (r.left - wrapRect.left) / wrapRect.width,
        y: (r.top - wrapRect.top) / wrapRect.height,
        w: r.width / wrapRect.width,
        h: r.height / wrapRect.height,
      });
    }
    if (!rects.length) return;
    (this.markups[pageNum] || (this.markups[pageNum] = [])).push({
      type: tool,
      color: tool === "highlight" ? this.state.hlColor : "#444",
      rects,
    });
    sel.removeAllRanges();
    this.drawMarkups(pageNum, wrap);
    this.saveMarkups();
  };

  renderSpread = () => {
    if (!this.index) return;
    const token = ++this.renderToken;
    const stage = this.stageRef.current,
      pageView = this.pageViewRef.current;
    if (!stage || !pageView) return;
    const nums = this.spreads[this.state.spreadIdx] || [];
    const descs = nums.map((n) => {
      const pg = this.index.pages[n - 1];
      return { pageNum: n, w: pg.w, h: pg.h, pg };
    });
    const totalW = descs.reduce((a, d) => a + d.w, 0);
    const maxH = Math.max(...descs.map((d) => d.h));
    const margin = 24;
    const fit = Math.min(
      (stage.clientWidth - margin * 2) / totalW,
      (stage.clientHeight - margin * 2) / maxH,
    );
    const scale = fit * this.state.zoom;

    pageView.innerHTML = "";
    // a single shadowed "spread" container hugs both page wraps (mirrors .pages shadow)
    const spread = document.createElement("div");
    spread.className = "spdf-spread";
    pageView.append(spread);

    for (const d of descs) {
      const cssW = Math.floor(d.w * scale),
        cssH = Math.floor(d.h * scale);
      const wrap = document.createElement("div");
      wrap.className = "spdf-pagewrap";
      wrap.style.width = cssW + "px";
      wrap.style.height = cssH + "px";
      spread.append(wrap);

      const img = document.createElement("img");
      img.src = this.base + d.pg.png + this.token;
      img.draggable = false;
      img.style.width = cssW + "px";
      img.style.height = cssH + "px";
      wrap.append(img);

      const tlDiv = document.createElement("div");
      tlDiv.className = "spdf-text";
      tlDiv.style.width = cssW + "px";
      tlDiv.style.height = cssH + "px";
      wrap.append(tlDiv);
      this.buildTextLayer(tlDiv, d.pg.text, scale);

      const mk = document.createElement("div");
      mk.className = "spdf-markup";
      wrap.append(mk);
      wrap.addEventListener("mouseup", (e) =>
        this.captureSelection(d.pageNum, wrap, e),
      );
      this.drawMarkups(d.pageNum, wrap);
    }
    if (token !== this.renderToken) return;
    // Scroll the pageview (the flex box that actually holds the spread), not the stage —
    // the stage stays fixed-size, so scrolling it does nothing. With scroll-safe centering
    // (see .spdf-pageview CSS) the overflow lands top/left-reachable on both axes.
    pageView.style.overflow = this.state.zoom > 1 ? "auto" : "hidden";

    // flank the book with the side-arrows (faithful to the HTML viewer: Back/prev just
    // left of the left page, Next just right of the right page).
    const sRect = spread.getBoundingClientRect(),
      stRect = stage.getBoundingClientRect();
    const pv = this.prevNavRef.current, // Back  (left)
      nv = this.nextNavRef.current; // Next  (right)
    if (pv) {
      pv.style.left = Math.max(0, sRect.left - stRect.left - 46) + "px";
      pv.style.right = "auto";
    }
    if (nv) {
      nv.style.left =
        Math.min(stRect.width - 46, sRect.right - stRect.left) + "px";
      nv.style.right = "auto";
    }
  };

  // ---- controls ----
  go = (dir) => {
    const next = Math.min(
      this.spreads.length - 1,
      Math.max(0, this.state.spreadIdx + dir),
    );
    if (next === this.state.spreadIdx) return;
    this.setState({ spreadIdx: next }, () => this.renderSpread());
  };
  goToPageNum = (p) => {
    if (!this.index) return;
    const page = Math.min(this.index.numPages, Math.max(1, p));
    const idx = Math.max(
      0,
      this.spreads.findIndex((s) => s.includes(page)),
    );
    if (idx === this.state.spreadIdx) return;
    this.setState({ spreadIdx: idx }, () => this.renderSpread());
  };
  onKey = (e) => {
    if (e.target && /INPUT|TEXTAREA|SELECT/.test(e.target.tagName)) return;
    if (e.key === "ArrowRight") this.go(1);
    else if (e.key === "ArrowLeft") this.go(-1);
    else if (e.key === "Escape" && this.props.closeBookView)
      this.props.closeBookView();
  };
  onResize = () => {
    clearTimeout(this._rt);
    this._rt = setTimeout(() => this.renderSpread(), 120);
  };
  zoomBy = (dz) =>
    this.setState(
      (s) => ({ zoom: Math.min(3, Math.max(0.5, +(s.zoom + dz).toFixed(2))) }),
      () => this.renderSpread(),
    );
  setTwoPage = (on) => {
    if (on === this.state.twoPage) return;
    const firstPage = (this.spreads[this.state.spreadIdx] || [1])[0];
    this.setState({ twoPage: on }, () => {
      this.spreads = this.buildSpreads(this.index.numPages);
      const idx = Math.max(
        0,
        this.spreads.findIndex((s) => s.includes(firstPage)),
      );
      this.setState({ spreadIdx: idx }, () => this.renderSpread());
    });
  };
  setTool = (tool) =>
    this.setState({ tool, hlMenuOpen: false }, () => this.refreshErasing());
  pickColor = (hex) =>
    this.setState({ hlColor: hex, tool: "highlight", hlMenuOpen: false });
  toggleHlMenu = () =>
    this.setState(
      (p) => ({ tool: "highlight", hlMenuOpen: !p.hlMenuOpen }),
      this.refreshErasing,
    );
  refreshErasing = () => {
    document
      .querySelectorAll(".spdf-markup")
      .forEach((l) =>
        l.classList.toggle("erasing", this.state.tool === "eraser"),
      );
  };

  // current-tool cursor, matching the HTML viewer's per-tool cursors
  toolCursor = () => {
    const t = this.state.tool;
    if (t === "highlight") {
      const c = HL_COLORS.find((c) => c.hex === this.state.hlColor) || HL_COLORS[0];
      return `url(${c.cursor}) 2 20, auto`;
    }
    if (t === "eraser") return `url(${cur_eraser}) 2 20, auto`;
    if (t === "underline" || t === "strike") return "crosshair";
    return null; // pointer => default / text
  };

  render() {
    const s = this.state;
    const nums = this.spreads[s.spreadIdx] || [1];
    const leftPage = nums[0] || 1;
    const cur = this.toolCursor();
    const rootStyle = cur ? { "--spdf-cursor": cur } : {};
    const cls = (active) =>
      "btn toolbar-page-btn" + (active ? " active" : "");
    return (
      <div className="spdf-root" style={rootStyle}>
        <style>{SPDF_CSS}</style>

        <div className="spdf-zoom">
          <button title="Zoom out" onClick={() => this.zoomBy(-0.15)}>
            <img alt="" src={icon_zoom_out} />
          </button>
          <button title="Zoom in" onClick={() => this.zoomBy(0.15)}>
            <img alt="" src={icon_zoom_in} />
          </button>
        </div>
        <button
          className="spdf-close"
          title="Close book"
          onClick={this.props.closeBookView}
        >
          <img alt="" src={icon_cross} />
        </button>

        <div className="spdf-stage" ref={this.stageRef}>
          <div className="spdf-nav prev" ref={this.prevNavRef}>
            <button
              disabled={s.spreadIdx <= 0}
              onClick={() => this.go(-1)}
              aria-label="Previous page"
            >
              <img alt="" src={icon_navarrow} />
            </button>
          </div>
          <div className="spdf-nav next" ref={this.nextNavRef}>
            <button
              disabled={s.spreadIdx >= this.spreads.length - 1}
              onClick={() => this.go(1)}
              aria-label="Next page"
            >
              <img alt="" src={icon_navarrow} />
            </button>
          </div>

          <div className="spdf-pageview" ref={this.pageViewRef} />
          {!s.loaded && !s.error && <div className="spdf-msg">Loading…</div>}
          {s.error && <div className="spdf-msg">Error: {s.error}</div>}
        </div>

        {/* bottom toolbar — mirrors the HTML eBook viewer */}
        <div style={{ display: "flex", justifyContent: "center" }}>
          <div className="toolbar row">
            <div className="tools-btn-container">
              <div className="iconTools">
                <button
                  className={"btn page-btn" + (s.tool === "pointer" ? " active" : "")}
                  title="Pointer (exit markup)"
                  onClick={() => this.setTool("pointer")}
                >
                  <img alt="" className="centered-img" src={icon_pointer} />
                </button>

                <div className="dropup item spdf-hlwrap">
                  <button
                    className={"btn page-btn" + (s.tool === "highlight" ? " active" : "")}
                    title="Highlight"
                    onClick={this.toggleHlMenu}
                  >
                    <img alt="" className="centered-img" src={icon_highlight} />
                  </button>
                  <div className={"spdf-hlmenu" + (s.hlMenuOpen ? " open" : "")}>
                    {HL_COLORS.map((c) => (
                      <div
                        key={c.name}
                        className="sw"
                        style={{ background: c.hex }}
                        title={c.name}
                        onClick={() => this.pickColor(c.hex)}
                      />
                    ))}
                  </div>
                </div>

                <button
                  className={cls(s.tool === "strike")}
                  title="Strikethrough"
                  onClick={() => this.setTool("strike")}
                >
                  <img alt="" className="centered-img" src={icon_strikethrough} />
                </button>
                <button
                  className={cls(s.tool === "underline")}
                  title="Underline"
                  onClick={() => this.setTool("underline")}
                >
                  <img alt="" className="centered-img" src={icon_underline} />
                </button>
                <button
                  className={cls(s.tool === "eraser")}
                  title="Eraser"
                  onClick={() => this.setTool("eraser")}
                >
                  <img alt="" className="centered-img" src={icon_eraser} />
                </button>
              </div>
            </div>

            <div className="item page-slider">
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Slider
                  min={1}
                  max={Math.max(1, s.numPages)}
                  step={1}
                  value={leftPage}
                  onChange={(v) => this.goToPageNum(Math.round(v))}
                />
                <p className="current-page-info">
                  <span className="bubble-text">{leftPage}</span>
                  of {s.numPages || "?"}
                </p>
              </div>
            </div>

            <div className="pages-btn-container">
              <button
                className="btn toolbar-page-btn"
                title="One page"
                onClick={() => this.setTwoPage(false)}
              >
                <img alt="" className="centered-img" src={icon_file} />
              </button>
              <button
                className="btn toolbar-page-btn"
                title="Two pages"
                onClick={() => this.setTwoPage(true)}
              >
                <img alt="" className="centered-img" src={icon_pages} />
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

const SPDF_CSS = `
.spdf-root { position: fixed; inset: 0; background: #f5f5f5; z-index: 1000; font-family: "Poppins", system-ui, sans-serif; }

/* round close button (mirrors .close-book) */
.spdf-close { position: fixed; right: 10px; top: 10px; z-index: 1110; width: 40px; height: 40px; padding: 0; border: none; border-radius: 500px; background: #fff; box-shadow: 0 0 20px rgba(0,0,0,.12); cursor: pointer; }
.spdf-close img { width: 16px; height: 16px; }

/* zoom pill (mirrors the header .drawer-wrapper / .zoom-btn) */
.spdf-zoom { position: fixed; top: 10px; left: 50%; transform: translateX(-50%); z-index: 1110; height: 40px; display: flex; align-items: center; background: #fff; border-radius: 100px; box-shadow: 0 0 20px rgba(0,0,0,.12); padding: 0 6px; }
.spdf-zoom button { background: transparent; border: none; padding: 6px 8px; cursor: pointer; display: flex; align-items: center; }
.spdf-zoom img { width: 20px; height: 20px; }

/* stage clears the fixed bottom toolbar */
.spdf-stage { position: absolute; top: 0; bottom: 96px; left: 0; right: 0; overflow: hidden; }
.spdf-pageview { position: absolute; inset: 0; display: flex; align-items: safe center; justify-content: safe center; cursor: var(--spdf-cursor, default); }
.spdf-msg { position: absolute; top: 40%; left: 0; right: 0; text-align: center; color: #888; }

/* shadowed spread + bordered white pages (mirrors .pages / .page) */
.spdf-spread { display: flex; box-shadow:
  0px 1.8568079471588135px 3.623039960861206px rgba(0,0,0,.02),
  0px 5.133836269378662px 10.017241477966309px rgba(0,0,0,.04),
  0px 12.360300064086914px 24.117658615112305px rgba(0,0,0,.05),
  0px 41px 80px rgba(0,0,0,.07); }
.spdf-pagewrap { position: relative; background: #fff; border: 1px solid #000; box-sizing: content-box; }
.spdf-pagewrap img { position: absolute; top: 0; left: 0; display: block; -webkit-user-select: none; user-select: none; -webkit-user-drag: none; user-drag: none; }

/* selectable transparent text layer */
.spdf-text { position: absolute; inset: 0; overflow: hidden; line-height: 1; }
.spdf-text span { color: transparent; position: absolute; white-space: pre; cursor: var(--spdf-cursor, text); transform-origin: 0 0; }
.spdf-text ::selection { background: rgba(120,170,255,.4); }

/* markup overlay */
.spdf-markup { position: absolute; inset: 0; pointer-events: none; }
.spdf-markup.erasing { pointer-events: auto; }
.spdf-mk { position: absolute; pointer-events: none; }
.spdf-markup.erasing .spdf-mk { pointer-events: auto; cursor: pointer; }
.spdf-markup.erasing .spdf-mk:hover { outline: 2px dashed #e23; outline-offset: 1px; }
.spdf-mk.hl { mix-blend-mode: multiply; border-radius: 2px; }

/* tall white side-arrows — faithful to the HTML viewer .pageIndex:
   both buttons rounded-right by default; Back/prev (left) flips the WHOLE button so its
   half-circle bulges left and arrow points left; Next (right) is unflipped. */
.spdf-nav { position: absolute; top: 50%; transform: translateY(-50%); z-index: 100; opacity: .6; }
.spdf-nav:hover { opacity: 1; }
.spdf-nav.prev { left: 0; right: auto; }
.spdf-nav.next { right: 0; left: auto; }
.spdf-nav button { height: 40vh; max-height: 400px; width: 46px; border: none; background: #fff; cursor: pointer; padding: 0 3px; display: flex; align-items: center; justify-content: center; border-radius: 0 100px 100px 0; }
.spdf-nav.prev button { transform: matrix(-1, 0, 0, 1, 0, 0); }
.spdf-nav button img { width: 30px; height: 56px; }
.spdf-nav button:hover { background: #f2f2f2; }
.spdf-nav button:disabled { opacity: .25; cursor: default; }

/* highlight color popup (sits above the highlight button in the toolbar) */
.spdf-hlwrap { position: relative; }
.spdf-hlmenu { position: absolute; left: 50%; transform: translateX(-50%); bottom: 58px; display: none; flex-direction: column; gap: 8px; background: #fff; border: 1px solid #ddd; border-radius: 10px; box-shadow: 0 4px 16px rgba(0,0,0,.18); padding: 8px; z-index: 20; }
.spdf-hlmenu.open { display: flex; }
.spdf-hlmenu .sw { width: 36px; height: 36px; border-radius: 50%; border: 2px solid #fff; box-shadow: 0 0 0 1px #ccc; cursor: pointer; }
.spdf-hlmenu .sw:hover { box-shadow: 0 0 0 2px #2699fe; }

/* visible active state on markup buttons (border-bottom blue, overrides !important page-btn border) */
.spdf-root .toolbar .page-btn.active,
.spdf-root .toolbar .toolbar-page-btn.active { border-bottom: 3px solid #2699fe !important; background: #fff !important; }
.spdf-root .toolbar .tools-btn-container { display: flex; }
.spdf-root .toolbar .centered-img { width: 24px; height: 24px; object-fit: contain; }
`;
